import { useEffect, useState } from "react";
import blankImage from "../../Assets/blank.png";
import { useParams } from "react-router-dom";
import UserApi from "../../Api/UserApi";
import { User } from "../../Models/User";
import {
  MdEmail,
  MdLocalPhone,
  MdLocationPin,
  MdDownload,
} from "react-icons/md";
import DOMPurify from "isomorphic-dompurify";
import Loader from "../Common/Loader";
import { Button } from "flowbite-react";

export default function UserProfilePublic() {
  const { id } = useParams();
  const [userData, setUserData] = useState<User | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getUser = () => {
    if (id) {
      setIsLoading(true);
      UserApi.getUserPublic(id)
        .then((res) => {
          setUserData(res.data);
          setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    } else {
      setUserData(null);
    }
  };

  useEffect(() => {
    getUser();
  }, [id]);

  const iconstyle = { color: "#5b9d30" };

  // Format phone number: (123) 456-7890
  const formatPhoneNumber = (phone?: string | null): string => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phone; // Return as-is if not 10 digits
  };

  // Generate vCard for contact download
  const downloadVCard = () => {
    if (!userData) return;

    const vCard = `BEGIN:VCARD
VERSION:3.0
FN:${userData.firstName} ${userData.lastName}
N:${userData.lastName};${userData.firstName};;;
ORG:${userData.member?.name || "EMDA"}
TITLE:${userData.jobTitle || ""}
TEL;TYPE=WORK,VOICE:${userData.phoneNumber || ""}
EMAIL:${userData.emailAddress || ""}
ADR;TYPE=WORK:;;${userData.address || ""};${userData.city || ""};${
      userData.state || ""
    };${userData.zip || ""};USA
URL:${window.location.href}
NOTE:${userData.bio?.replace(/<[^>]*>/g, "").substring(0, 500) || ""}
END:VCARD`;

    const blob = new Blob([vCard], { type: "text/vcard" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${userData.firstName}_${userData.lastName}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="w-full">
        <div>
          <div className="w-full p-2">
            <div className="lg:flex no-wrap md:-mx-2 ">
              <div className="w-full lg:w-1/4 md:mx-2">
                <div className="rounded-md">
                  <img
                    src={userData?.image?.url ?? blankImage}
                    alt="..."
                    className="object-contain justify-self-center h-72"
                  />
                </div>
              </div>
              <div className="w-full lg:w-3/4 mx-2">
                <div className="bg-white p-3">
                  <div className="flex flex-col lg:flex-row items-end space-x-2 font-bold text-black py-2">
                    <div className="w-full">
                      <span className="tracking-wide uppercase text-3xl">
                        {userData?.firstName} {userData?.lastName}
                      </span>
                    </div>
                    <div>
                      <Button
                        onClick={downloadVCard}
                        className="buttonMain mb-2"
                        size="sm"
                      >
                        <MdDownload className="mr-2" size={18} />
                        Download Contact
                      </Button>
                    </div>
                  </div>
                  <hr className="bg-gray-300 h-[5px]"></hr>
                  <div className="text-gray-500">
                    <div className="grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl">
                      <div className="flex">
                        <div className="px-1 content-center">
                          <MdLocalPhone style={iconstyle} />
                        </div>
                        <div className="font-bold">Phone:</div>
                      </div>
                      <div className="pl-1">
                        {userData?.phoneNumber && (
                          <a
                            href={`tel:${userData.phoneNumber.replace(
                              /\D/g,
                              ""
                            )}`}
                            className="text-mainButton hover:text-green-700 hover:underline"
                          >
                            {formatPhoneNumber(userData.phoneNumber)}
                          </a>
                        )}
                      </div>
                    </div>
                    <hr className="bg-gray-300 h-[2px]"></hr>
                    <div className="grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl">
                      <div className="flex">
                        <div className="px-1 content-center">
                          <MdEmail style={iconstyle} />
                        </div>
                        <div className="font-bold">Email:</div>
                      </div>
                      <div className="pl-1">
                        {userData?.emailAddress && (
                          <a
                            href={`mailto:${userData.emailAddress}`}
                            className="text-mainButton hover:text-green-700 hover:underline break-all"
                          >
                            {userData.emailAddress}
                          </a>
                        )}
                      </div>
                    </div>
                    <hr className="bg-gray-300 h-[2px]"></hr>
                    <div className="grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl">
                      <div className="flex">
                        <div className="px-1 content-center">
                          <MdLocationPin style={iconstyle} />
                        </div>
                        <div className="font-bold content-center">Address:</div>
                      </div>
                      <div className="pl-1 text-mainButton">
                        {userData?.address} {userData?.city}, {userData?.state}{" "}
                        {userData?.zip}
                      </div>
                    </div>
                    <hr className="bg-gray-300 h-[2px]"></hr>
                    <div className="flex flex-col md:grid md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl">
                      <div className="flex">
                        {/* <div className='px-1 content-center'>
                        <FaTwitter style={iconstyle} />
                      </div> */}
                        <div className="font-bold">Bio:</div>
                      </div>
                      <div className="pl-1 text-mainButton whitespace-pre-line">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(userData?.bio ?? ""),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* <!-- End of about section --> */}
                {/* <!-- End of profile tab --> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
